import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Divider,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Slider,
  Switch,
  Tab,
  Tabs,
  Textarea,
  useDisclosure,
} from '@nextui-org/react';
import {
  AlertCircle,
  AtSign,
  Bell,
  Bookmark,
  Camera,
  CheckCircle2,
  ExternalLink,
  Eye,
  EyeOff,
  Globe,
  Hash,
  Image as ImageIcon,
  KeyRound,
  LogOut,
  Mail,
  MapPin,
  Music,
  Pencil,
  Phone,
  PlayCircle,
  Plus,
  Save,
  ShieldAlert,
  Target,
  Trash2,
  Upload,
  User as UserIcon,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import userService, {
  ChangePasswordPayload,
  DartcloudUser,
  DartSetup,
  GeocodeResult,
  PublicProfile,
  SocialMediaLinks,
  UpdateUserPayload,
} from '../services/userService';
import AddressSearchInput from '../components/AddressSearchInput';

type FeedbackKind = 'success' | 'error';
interface Feedback {
  kind: FeedbackKind;
  message: string;
}

const FeedbackBanner: React.FC<{ feedback: Feedback | null }> = ({ feedback }) => {
  if (!feedback) return null;
  const isSuccess = feedback.kind === 'success';
  return (
    <div
      className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${
        isSuccess
          ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
          : 'border-red-500/40 bg-red-500/10 text-red-300'
      }`}
    >
      {isSuccess ? (
        <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
      ) : (
        <AlertCircle size={16} className="mt-0.5 shrink-0" />
      )}
      <span>{feedback.message}</span>
    </div>
  );
};

const labelClass = 'text-gray-300';
const inputWrapperClass =
  'bg-zinc-950 border-zinc-700 data-[hover=true]:border-zinc-600 group-data-[focus=true]:border-[#3fced1]';
const inputClassNames = {
  inputWrapper: inputWrapperClass,
  input: 'text-white placeholder:text-gray-500',
  label: labelClass,
};

const formatTimestamp = (ts?: number | null): string => {
  if (!ts) return '–';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '–';
  return d.toLocaleDateString('de-DE', { year: 'numeric', month: 'long', day: 'numeric' });
};

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        const commaIdx = result.indexOf(',');
        resolve(commaIdx >= 0 ? result.substring(commaIdx + 1) : result);
      } else {
        reject(new Error('Datei konnte nicht gelesen werden.'));
      }
    };
    reader.onerror = () => reject(reader.error || new Error('Datei konnte nicht gelesen werden.'));
    reader.readAsDataURL(file);
  });

/** Liest eine Datei als vollständige Data-URL (inkl. "data:image/...;base64,"-Prefix). */
const fileToDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        resolve(result);
      } else {
        reject(new Error('Datei konnte nicht gelesen werden.'));
      }
    };
    reader.onerror = () => reject(reader.error || new Error('Datei konnte nicht gelesen werden.'));
    reader.readAsDataURL(file);
  });

const validateImageFile = (file: File): string | null => {
  if (!file.type.startsWith('image/')) return 'Bitte eine Bilddatei auswählen.';
  if (file.size > 5 * 1024 * 1024) return 'Datei darf maximal 5 MB groß sein.';
  return null;
};

const EMPTY_SOCIAL: SocialMediaLinks = {
  instagram: '',
  facebook: '',
  twitter: '',
  tiktok: '',
  youtube: '',
};

const ProfilePage: React.FC = () => {
  const { logout, user: authUser, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [user, setUser] = useState<DartcloudUser | null>(null);
  const [publicProfile, setPublicProfile] = useState<PublicProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [personalForm, setPersonalForm] = useState<UpdateUserPayload>({});
  const [isSavingPersonal, setIsSavingPersonal] = useState(false);
  const [personalFeedback, setPersonalFeedback] = useState<Feedback | null>(null);

  const [notificationForm, setNotificationForm] = useState<UpdateUserPayload>({});
  const [notificationAddressText, setNotificationAddressText] = useState('');
  const [notificationLatLng, setNotificationLatLng] = useState<{
    latitude: number | null;
    longitude: number | null;
  }>({ latitude: null, longitude: null });
  const [isSavingNotifications, setIsSavingNotifications] = useState(false);
  const [notificationsFeedback, setNotificationsFeedback] = useState<Feedback | null>(null);

  const [publicForm, setPublicForm] = useState<Partial<PublicProfile>>({});
  const [socialForm, setSocialForm] = useState<SocialMediaLinks>({ ...EMPTY_SOCIAL });
  const [isSavingPublic, setIsSavingPublic] = useState(false);
  const [publicFeedback, setPublicFeedback] = useState<Feedback | null>(null);

  // Header-Bild (öffentliches Profil)
  const headerFileInputRef = useRef<HTMLInputElement | null>(null);
  const [isUploadingHeader, setIsUploadingHeader] = useState(false);
  const [headerFeedback, setHeaderFeedback] = useState<Feedback | null>(null);

  // Dart-Setups
  const setupModal = useDisclosure();
  const setupFileInputRef = useRef<HTMLInputElement | null>(null);
  const [editingSetup, setEditingSetup] = useState<DartSetup | null>(null);
  const [setupForm, setSetupForm] = useState<{ title: string; subtitle: string; description: string }>({
    title: '',
    subtitle: '',
    description: '',
  });
  const [setupImagePreview, setSetupImagePreview] = useState<string | null>(null);
  const [setupImageDataUrl, setSetupImageDataUrl] = useState<string | null>(null);
  const [isSavingSetup, setIsSavingSetup] = useState(false);
  const [setupModalFeedback, setSetupModalFeedback] = useState<Feedback | null>(null);
  const [setupListFeedback, setSetupListFeedback] = useState<Feedback | null>(null);
  const [deletingSetupId, setDeletingSetupId] = useState<string | null>(null);

  const [pwForm, setPwForm] = useState<ChangePasswordPayload & { confirm: string }>({
    currentPassword: '',
    newPassword: '',
    confirm: '',
  });
  const [showPw, setShowPw] = useState(false);
  const [isChangingPw, setIsChangingPw] = useState(false);
  const [pwFeedback, setPwFeedback] = useState<Feedback | null>(null);

  const deleteModal = useDisclosure();
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteFeedback, setDeleteFeedback] = useState<Feedback | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imageFeedback, setImageFeedback] = useState<Feedback | null>(null);

  const applyUser = useCallback((me: DartcloudUser) => {
    setUser(me);
    setPersonalForm({
      firstname: me?.personalData?.firstname ?? '',
      lastname: me?.personalData?.lastname ?? '',
      phone: me?.personalData?.phone ?? '',
      username: me?.username ?? '',
      chatUsername: me?.chatUsername ?? '',
      country: me?.personalData?.country ?? me?.personalData?.address?.country ?? '',
      language: me?.language ?? '',
    });
    setNotificationForm({
      notificationsEnabled: me?.notificationsEnabled ?? true,
      notificationsEnabledFavourites: me?.notificationsEnabledFavourites ?? true,
      notificationsEnabledNews: me?.notificationsEnabledNews ?? true,
      notificationsEnabledOwner: me?.notificationsEnabledOwner ?? true,
      notificationDistance: me?.notificationDistance ?? 100,
    });
    setNotificationAddressText(me?.personalData?.address?.label ?? '');
    setNotificationLatLng({
      latitude: me?.personalData?.address?.latitude ?? null,
      longitude: me?.personalData?.address?.longitude ?? null,
    });
  }, []);

  const reloadUser = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const me = await userService.getCurrentUser();
      applyUser(me);

      const pub = await userService.getPublicProfile();
      setPublicProfile(pub);
      setPublicForm({
        isPublic: pub?.isPublic ?? true,
        bio: pub?.bio ?? '',
      });
      setSocialForm({
        instagram: pub?.socialMedia?.instagram ?? '',
        facebook: pub?.socialMedia?.facebook ?? '',
        twitter: pub?.socialMedia?.twitter ?? '',
        tiktok: pub?.socialMedia?.tiktok ?? '',
        youtube: pub?.socialMedia?.youtube ?? '',
      });
    } catch (err: any) {
      setLoadError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Profil konnte nicht geladen werden.'
      );
    } finally {
      setIsLoading(false);
    }
  }, [applyUser]);

  useEffect(() => {
    reloadUser();
  }, [reloadUser]);

  const displayName = useMemo(() => {
    const fn = user?.personalData?.firstname || authUser?.firstname || '';
    const ln = user?.personalData?.lastname || authUser?.lastname || '';
    const full = `${fn} ${ln}`.trim();
    return full || user?.username || authUser?.username || user?.personalData?.email || 'Profil';
  }, [user, authUser]);

  const initials = useMemo(() => {
    const fn = user?.personalData?.firstname || authUser?.firstname || '';
    const ln = user?.personalData?.lastname || authUser?.lastname || '';
    const combined = `${fn.charAt(0)}${ln.charAt(0)}`.toUpperCase();
    return combined || (user?.username || user?.personalData?.email || '?').charAt(0).toUpperCase();
  }, [user, authUser]);

  const avatarUrl = useMemo(() => userService.getFileUrl(user?.profileImage), [user?.profileImage]);

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  const triggerFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setImageFeedback({ kind: 'error', message: 'Bitte eine Bilddatei auswählen.' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setImageFeedback({ kind: 'error', message: 'Datei darf maximal 5 MB groß sein.' });
      return;
    }
    setIsUploadingImage(true);
    setImageFeedback(null);
    try {
      const base64 = await fileToBase64(file);
      const uploaded = await userService.uploadFile(base64);
      const fileName = uploaded?.name;
      if (!fileName) throw new Error('Upload fehlgeschlagen.');
      const updated = await userService.updateUser({ profileImage: fileName });
      applyUser(updated);
      await refreshUser();
      setImageFeedback({ kind: 'success', message: 'Profilbild aktualisiert.' });
    } catch (err: any) {
      setImageFeedback({
        kind: 'error',
        message:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Profilbild konnte nicht hochgeladen werden.',
      });
    } finally {
      setIsUploadingImage(false);
    }
  };

  const savePersonal = async () => {
    setIsSavingPersonal(true);
    setPersonalFeedback(null);
    try {
      const updated = await userService.updateUser(personalForm);
      applyUser(updated);
      setPersonalFeedback({ kind: 'success', message: 'Persönliche Daten gespeichert.' });
    } catch (err: any) {
      setPersonalFeedback({
        kind: 'error',
        message:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Speichern fehlgeschlagen.',
      });
    } finally {
      setIsSavingPersonal(false);
    }
  };

  const handleAddressSelect = (result: GeocodeResult) => {
    setNotificationLatLng({
      latitude: result.latitude ?? null,
      longitude: result.longitude ?? null,
    });
    setNotificationsFeedback(null);
  };

  const saveNotifications = async () => {
    setIsSavingNotifications(true);
    setNotificationsFeedback(null);
    try {
      const payload: UpdateUserPayload = {
        notificationsEnabled: notificationForm.notificationsEnabled,
        notificationsEnabledFavourites: notificationForm.notificationsEnabledFavourites,
        notificationsEnabledNews: notificationForm.notificationsEnabledNews,
        notificationsEnabledOwner: notificationForm.notificationsEnabledOwner,
        notificationDistance: notificationForm.notificationDistance,
      };
      if (
        notificationAddressText &&
        notificationLatLng.latitude != null &&
        notificationLatLng.longitude != null
      ) {
        payload.addressLabel = notificationAddressText;
        payload.latitude = notificationLatLng.latitude;
        payload.longitude = notificationLatLng.longitude;
      }
      const updated = await userService.updateUser(payload);
      applyUser(updated);
      setNotificationsFeedback({
        kind: 'success',
        message: 'Benachrichtigungs-Einstellungen gespeichert.',
      });
    } catch (err: any) {
      setNotificationsFeedback({
        kind: 'error',
        message:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Speichern fehlgeschlagen.',
      });
    } finally {
      setIsSavingNotifications(false);
    }
  };

  const savePublicProfile = async () => {
    setIsSavingPublic(true);
    setPublicFeedback(null);
    try {
      const updated = await userService.updatePublicProfile({
        isPublic: publicForm.isPublic ?? false,
        bio: publicForm.bio ?? '',
        socialMedia: {
          instagram: socialForm.instagram?.trim() || null,
          facebook: socialForm.facebook?.trim() || null,
          twitter: socialForm.twitter?.trim() || null,
          tiktok: socialForm.tiktok?.trim() || null,
          youtube: socialForm.youtube?.trim() || null,
        },
      });
      setPublicProfile(updated);
      setPublicFeedback({ kind: 'success', message: 'Öffentliches Profil gespeichert.' });
    } catch (err: any) {
      setPublicFeedback({
        kind: 'error',
        message:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Speichern fehlgeschlagen.',
      });
    } finally {
      setIsSavingPublic(false);
    }
  };

  // ============================================
  // Header-Bild (öffentliches Profil)
  // ============================================

  const handleHeaderFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const validationError = validateImageFile(file);
    if (validationError) {
      setHeaderFeedback({ kind: 'error', message: validationError });
      return;
    }
    setIsUploadingHeader(true);
    setHeaderFeedback(null);
    try {
      const dataUrl = await fileToDataUrl(file);
      const result = await userService.updateHeaderImage(dataUrl);
      setPublicProfile((prev) =>
        prev ? { ...prev, headerImage: result?.headerImage ?? null } : prev
      );
      setHeaderFeedback({ kind: 'success', message: 'Header-Bild aktualisiert.' });
    } catch (err: any) {
      setHeaderFeedback({
        kind: 'error',
        message:
          err?.response?.data?.message ||
          err?.response?.data ||
          'Header-Bild konnte nicht hochgeladen werden.',
      });
    } finally {
      setIsUploadingHeader(false);
    }
  };

  const removeHeaderImage = async () => {
    setIsUploadingHeader(true);
    setHeaderFeedback(null);
    try {
      await userService.deleteHeaderImage();
      setPublicProfile((prev) => (prev ? { ...prev, headerImage: null } : prev));
      setHeaderFeedback({ kind: 'success', message: 'Header-Bild entfernt.' });
    } catch (err: any) {
      setHeaderFeedback({
        kind: 'error',
        message: 'Header-Bild konnte nicht entfernt werden.',
      });
    } finally {
      setIsUploadingHeader(false);
    }
  };

  // ============================================
  // Dart-Setups
  // ============================================

  const dartSetups: DartSetup[] = Array.isArray(publicProfile?.dartSetups)
    ? (publicProfile?.dartSetups as DartSetup[])
    : [];

  const openCreateSetup = () => {
    setEditingSetup(null);
    setSetupForm({ title: '', subtitle: '', description: '' });
    setSetupImagePreview(null);
    setSetupImageDataUrl(null);
    setSetupModalFeedback(null);
    setupModal.onOpen();
  };

  const openEditSetup = (setup: DartSetup) => {
    setEditingSetup(setup);
    setSetupForm({
      title: setup.title ?? '',
      subtitle: setup.subtitle ?? '',
      description: setup.description ?? '',
    });
    setSetupImagePreview(userService.getFileUrl(setup.image));
    setSetupImageDataUrl(null);
    setSetupModalFeedback(null);
    setupModal.onOpen();
  };

  const handleSetupFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const validationError = validateImageFile(file);
    if (validationError) {
      setSetupModalFeedback({ kind: 'error', message: validationError });
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      setSetupImageDataUrl(dataUrl);
      setSetupImagePreview(dataUrl);
      setSetupModalFeedback(null);
    } catch {
      setSetupModalFeedback({ kind: 'error', message: 'Datei konnte nicht gelesen werden.' });
    }
  };

  const saveSetup = async () => {
    setSetupModalFeedback(null);
    if (!setupForm.title.trim()) {
      setSetupModalFeedback({ kind: 'error', message: 'Bitte einen Titel angeben.' });
      return;
    }
    if (!editingSetup && !setupImageDataUrl) {
      setSetupModalFeedback({ kind: 'error', message: 'Bitte ein Bild auswählen.' });
      return;
    }
    setIsSavingSetup(true);
    try {
      if (editingSetup?.id) {
        const updated = await userService.updateDartSetup(editingSetup.id, {
          title: setupForm.title.trim(),
          subtitle: setupForm.subtitle.trim() || null,
          description: setupForm.description.trim() || null,
          ...(setupImageDataUrl ? { image: setupImageDataUrl } : {}),
        });
        setPublicProfile((prev) => {
          if (!prev) return prev;
          const setups = Array.isArray(prev.dartSetups) ? prev.dartSetups : [];
          return {
            ...prev,
            dartSetups: setups.map((s: DartSetup) =>
              s.id === editingSetup.id ? { ...s, ...(updated ?? {}) } : s
            ),
          };
        });
      } else {
        const created = await userService.addDartSetup({
          image: setupImageDataUrl as string,
          title: setupForm.title.trim(),
          subtitle: setupForm.subtitle.trim() || null,
          description: setupForm.description.trim() || null,
        });
        if (created) {
          setPublicProfile((prev) => {
            if (!prev) return prev;
            const setups = Array.isArray(prev.dartSetups) ? prev.dartSetups : [];
            return { ...prev, dartSetups: [...setups, created] };
          });
        }
      }
      setupModal.onClose();
      setSetupListFeedback({
        kind: 'success',
        message: editingSetup ? 'Setup aktualisiert.' : 'Setup erstellt.',
      });
    } catch (err: any) {
      setSetupModalFeedback({
        kind: 'error',
        message:
          err?.response?.data?.message ||
          (typeof err?.response?.data === 'string' ? err.response.data : null) ||
          'Setup konnte nicht gespeichert werden.',
      });
    } finally {
      setIsSavingSetup(false);
    }
  };

  const removeSetup = async (setup: DartSetup) => {
    if (!setup?.id) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Setup "${setup.title ?? ''}" wirklich löschen?`)) return;
    setDeletingSetupId(setup.id);
    setSetupListFeedback(null);
    try {
      await userService.deleteDartSetup(setup.id);
      setPublicProfile((prev) => {
        if (!prev) return prev;
        const setups = Array.isArray(prev.dartSetups) ? prev.dartSetups : [];
        return { ...prev, dartSetups: setups.filter((s: DartSetup) => s.id !== setup.id) };
      });
      setSetupListFeedback({ kind: 'success', message: 'Setup gelöscht.' });
    } catch (err: any) {
      setSetupListFeedback({
        kind: 'error',
        message: 'Setup konnte nicht gelöscht werden.',
      });
    } finally {
      setDeletingSetupId(null);
    }
  };

  const changePassword = async () => {
    setPwFeedback(null);
    if (!pwForm.currentPassword) {
      setPwFeedback({ kind: 'error', message: 'Bitte aktuelles Passwort eingeben.' });
      return;
    }
    if (!pwForm.newPassword || pwForm.newPassword.length < 6) {
      setPwFeedback({ kind: 'error', message: 'Neues Passwort muss mindestens 6 Zeichen lang sein.' });
      return;
    }
    if (pwForm.newPassword !== pwForm.confirm) {
      setPwFeedback({ kind: 'error', message: 'Die neuen Passwörter stimmen nicht überein.' });
      return;
    }
    setIsChangingPw(true);
    try {
      await userService.changePassword({
        currentPassword: pwForm.currentPassword,
        newPassword: pwForm.newPassword,
      });
      setPwForm({ currentPassword: '', newPassword: '', confirm: '' });
      setPwFeedback({ kind: 'success', message: 'Passwort erfolgreich geändert.' });
    } catch (err: any) {
      setPwFeedback({
        kind: 'error',
        message:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Passwort konnte nicht geändert werden.',
      });
    } finally {
      setIsChangingPw(false);
    }
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    setDeleteFeedback(null);
    try {
      await userService.deleteAccount();
      logout();
      navigate('/', { replace: true });
    } catch (err: any) {
      setDeleteFeedback({
        kind: 'error',
        message:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Account konnte nicht gelöscht werden.',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const savedCounts = {
    tournaments: user?.savedTournaments?.length ?? 0,
    series: user?.savedSeries?.length ?? 0,
    clubs: user?.savedClubs?.length ?? 0,
    pubs: user?.savedPubs?.length ?? 0,
    shops: user?.savedShops?.length ?? 0,
  };

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-gray-400">
        Profil wird geladen …
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <FeedbackBanner feedback={{ kind: 'error', message: loadError }} />
        <Button className="mt-4" onPress={reloadUser} variant="bordered">
          Erneut versuchen
        </Button>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Mein Profil | DartCloud</title>
        <meta name="description" content="Verwalte dein DartCloud-Profil, Einstellungen und Benachrichtigungen." />
      </Helmet>

      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        onChange={handleFileSelected}
        className="hidden"
      />
      <input
        type="file"
        accept="image/*"
        ref={headerFileInputRef}
        onChange={handleHeaderFileSelected}
        className="hidden"
      />
      <input
        type="file"
        accept="image/*"
        ref={setupFileInputRef}
        onChange={handleSetupFileSelected}
        className="hidden"
      />

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Profil-Header */}
        <Card className="bg-zinc-900/80 border border-zinc-800 mb-6">
          <CardBody className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="relative shrink-0">
                <Avatar
                  src={avatarUrl || undefined}
                  name={initials}
                  className="h-20 w-20 text-large bg-[#3fced1] text-black"
                  showFallback
                />
                <button
                  type="button"
                  onClick={triggerFilePicker}
                  disabled={isUploadingImage}
                  className="absolute -bottom-1 -right-1 rounded-full bg-[#3fced1] hover:bg-[#35b5b8] text-black p-2 shadow-md disabled:opacity-60"
                  aria-label="Profilbild ändern"
                  title="Profilbild ändern"
                >
                  <Camera size={14} />
                </button>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-2xl font-bold text-white truncate">{displayName}</h1>
                  {user?.verified && (
                    <Chip
                      size="sm"
                      className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                      startContent={<CheckCircle2 size={14} />}
                    >
                      Verifiziert
                    </Chip>
                  )}
                  {user?.role && user.role !== 'USER' && (
                    <Chip size="sm" className="bg-[#3fced1]/20 text-[#3fced1] border border-[#3fced1]/40">
                      {user.role}
                    </Chip>
                  )}
                </div>
                <p className="text-sm text-gray-400 mt-1">
                  @{user?.username || '—'} · {user?.personalData?.email || '—'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Mitglied seit {formatTimestamp(user?.createdAt)}
                </p>
                {(imageFeedback || isUploadingImage) && (
                  <div className="mt-2 max-w-md">
                    {isUploadingImage ? (
                      <p className="text-xs text-gray-400">Bild wird hochgeladen …</p>
                    ) : (
                      <FeedbackBanner feedback={imageFeedback} />
                    )}
                  </div>
                )}
              </div>
              <div className="flex gap-2 self-stretch sm:self-auto">
                <Button
                  variant="bordered"
                  className="border-zinc-700 text-gray-300"
                  startContent={<LogOut size={16} />}
                  onPress={handleLogout}
                >
                  Abmelden
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>

        <Tabs
          aria-label="Profil-Bereiche"
          color="primary"
          variant="underlined"
          classNames={{
            tabList: 'gap-6 w-full relative rounded-none p-0 border-b border-zinc-800',
            cursor: 'w-full bg-[#3fced1]',
            tab: 'max-w-fit px-0 h-12',
            tabContent: 'group-data-[selected=true]:text-[#3fced1] text-gray-400',
          }}
        >
          {/* Allgemein */}
          <Tab
            key="general"
            title={
              <div className="flex items-center gap-2">
                <UserIcon size={16} />
                <span>Allgemein</span>
              </div>
            }
          >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
              <Card className="bg-zinc-900/80 border border-zinc-800 lg:col-span-2">
                <CardHeader className="px-6 pt-6 pb-2">
                  <h2 className="text-lg font-semibold text-white">Persönliche Daten</h2>
                </CardHeader>
                <CardBody className="px-6 pb-6 pt-2 flex flex-col gap-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label="Vorname"
                      value={personalForm.firstname ?? ''}
                      onValueChange={(v) => setPersonalForm((s) => ({ ...s, firstname: v }))}
                      startContent={<UserIcon size={16} className="text-gray-400" />}
                      variant="bordered"
                      classNames={inputClassNames}
                    />
                    <Input
                      label="Nachname"
                      value={personalForm.lastname ?? ''}
                      onValueChange={(v) => setPersonalForm((s) => ({ ...s, lastname: v }))}
                      startContent={<UserIcon size={16} className="text-gray-400" />}
                      variant="bordered"
                      classNames={inputClassNames}
                    />
                  </div>
                  <Input
                    label="E-Mail (nur Anzeige)"
                    value={user?.personalData?.email ?? ''}
                    isReadOnly
                    isDisabled
                    startContent={<Mail size={16} className="text-gray-400" />}
                    variant="bordered"
                    classNames={inputClassNames}
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label="Benutzername"
                      value={personalForm.username ?? ''}
                      onValueChange={(v) => setPersonalForm((s) => ({ ...s, username: v }))}
                      startContent={<UserIcon size={16} className="text-gray-400" />}
                      variant="bordered"
                      classNames={inputClassNames}
                    />
                    <Input
                      label="Chat-Name"
                      value={personalForm.chatUsername ?? ''}
                      onValueChange={(v) => setPersonalForm((s) => ({ ...s, chatUsername: v }))}
                      startContent={<UserIcon size={16} className="text-gray-400" />}
                      variant="bordered"
                      classNames={inputClassNames}
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label="Telefon"
                      value={personalForm.phone ?? ''}
                      onValueChange={(v) => setPersonalForm((s) => ({ ...s, phone: v }))}
                      startContent={<Phone size={16} className="text-gray-400" />}
                      variant="bordered"
                      classNames={inputClassNames}
                    />
                    <Input
                      label="Land"
                      value={personalForm.country ?? ''}
                      onValueChange={(v) => setPersonalForm((s) => ({ ...s, country: v }))}
                      startContent={<Globe size={16} className="text-gray-400" />}
                      variant="bordered"
                      classNames={inputClassNames}
                    />
                  </div>

                  <FeedbackBanner feedback={personalFeedback} />

                  <div className="flex justify-end">
                    <Button
                      className="bg-[#3fced1] hover:bg-[#35b5b8] text-black font-semibold"
                      startContent={<Save size={16} />}
                      isLoading={isSavingPersonal}
                      onPress={savePersonal}
                    >
                      Speichern
                    </Button>
                  </div>
                </CardBody>
              </Card>

              <Card className="bg-zinc-900/80 border border-zinc-800 h-fit">
                <CardHeader className="px-6 pt-6 pb-2">
                  <h2 className="text-lg font-semibold text-white">Meine Inhalte</h2>
                </CardHeader>
                <CardBody className="px-6 pb-6 pt-2 flex flex-col gap-1">
                  <RouterLink
                    to="/meine/turniere"
                    className="flex items-center justify-between py-2 px-2 rounded hover:bg-zinc-800/60 text-gray-300 hover:text-white transition-colors"
                  >
                    <span>Meine Turniere</span>
                    <span className="text-xs text-gray-500">→</span>
                  </RouterLink>
                  <RouterLink
                    to="/meine/serien"
                    className="flex items-center justify-between py-2 px-2 rounded hover:bg-zinc-800/60 text-gray-300 hover:text-white transition-colors"
                  >
                    <span>Meine Turnierserien</span>
                    <span className="text-xs text-gray-500">→</span>
                  </RouterLink>
                  <RouterLink
                    to="/meine/vereine"
                    className="flex items-center justify-between py-2 px-2 rounded hover:bg-zinc-800/60 text-gray-300 hover:text-white transition-colors"
                  >
                    <span>Meine Vereine</span>
                    <span className="text-xs text-gray-500">→</span>
                  </RouterLink>
                  <RouterLink
                    to="/meine/spielstaetten"
                    className="flex items-center justify-between py-2 px-2 rounded hover:bg-zinc-800/60 text-gray-300 hover:text-white transition-colors"
                  >
                    <span>Meine Spielstätten</span>
                    <span className="text-xs text-gray-500">→</span>
                  </RouterLink>
                  <RouterLink
                    to="/meine/shops"
                    className="flex items-center justify-between py-2 px-2 rounded hover:bg-zinc-800/60 text-gray-300 hover:text-white transition-colors"
                  >
                    <span>Meine Shops</span>
                    <span className="text-xs text-gray-500">→</span>
                  </RouterLink>

                  <Divider className="my-3 bg-zinc-800" />

                  <div className="flex items-center gap-2 mb-1 text-xs uppercase tracking-widest text-gray-500">
                    <Bookmark size={14} />
                    Favoriten
                  </div>
                  <p className="text-sm text-gray-400">
                    {savedCounts.tournaments} Turniere · {savedCounts.series} Serien ·{' '}
                    {savedCounts.clubs} Vereine · {savedCounts.pubs} Spielstätten ·{' '}
                    {savedCounts.shops} Shops
                  </p>
                  <RouterLink
                    to="/meine/favoriten"
                    className="text-sm text-[#3fced1] hover:text-[#35b5b8] mt-1"
                  >
                    Favoriten verwalten →
                  </RouterLink>
                </CardBody>
              </Card>
            </div>
          </Tab>

          {/* Benachrichtigungen */}
          <Tab
            key="notifications"
            title={
              <div className="flex items-center gap-2">
                <Bell size={16} />
                <span>Benachrichtigungen</span>
              </div>
            }
          >
            <Card className="bg-zinc-900/80 border border-zinc-800 mt-6">
              <CardHeader className="px-6 pt-6 pb-2">
                <h2 className="text-lg font-semibold text-white">
                  Benachrichtigungs-Einstellungen
                </h2>
              </CardHeader>
              <CardBody className="px-6 pb-6 pt-2 flex flex-col gap-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">Benachrichtigungen aktiv</p>
                    <p className="text-xs text-gray-400">
                      Steuert ob du überhaupt Push- und E-Mail-Benachrichtigungen erhältst.
                    </p>
                  </div>
                  <Switch
                    isSelected={!!notificationForm.notificationsEnabled}
                    onValueChange={(v) =>
                      setNotificationForm((s) => ({ ...s, notificationsEnabled: v }))
                    }
                    color="primary"
                  />
                </div>
                <Divider className="bg-zinc-800" />
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">Favoriten</p>
                    <p className="text-xs text-gray-400">
                      Updates zu deinen gespeicherten Turnieren, Vereinen, etc.
                    </p>
                  </div>
                  <Switch
                    isSelected={!!notificationForm.notificationsEnabledFavourites}
                    onValueChange={(v) =>
                      setNotificationForm((s) => ({ ...s, notificationsEnabledFavourites: v }))
                    }
                    color="primary"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">News & Artikel</p>
                    <p className="text-xs text-gray-400">Erfahre, wenn neue News erscheinen.</p>
                  </div>
                  <Switch
                    isSelected={!!notificationForm.notificationsEnabledNews}
                    onValueChange={(v) =>
                      setNotificationForm((s) => ({ ...s, notificationsEnabledNews: v }))
                    }
                    color="primary"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">Eigene Einträge (Owner)</p>
                    <p className="text-xs text-gray-400">
                      Benachrichtigungen für Inhalte, die du selbst verwaltest.
                    </p>
                  </div>
                  <Switch
                    isSelected={!!notificationForm.notificationsEnabledOwner}
                    onValueChange={(v) =>
                      setNotificationForm((s) => ({ ...s, notificationsEnabledOwner: v }))
                    }
                    color="primary"
                  />
                </div>

                <Divider className="bg-zinc-800" />

                <div className="flex flex-col gap-2">
                  <p className="text-white font-medium">Standort für Benachrichtigungen</p>
                  <p className="text-xs text-gray-400">
                    Dieser Standort wird verwendet, um dich über neue Events in deiner Umgebung
                    zu informieren.
                  </p>
                  <AddressSearchInput
                    value={notificationAddressText}
                    onValueChange={setNotificationAddressText}
                    onSelect={handleAddressSelect}
                    label="Adresse"
                    placeholder="Stadt oder vollständige Adresse"
                    classNames={inputClassNames}
                  />
                  {notificationLatLng.latitude != null && notificationLatLng.longitude != null && (
                    <div className="flex items-center gap-2 text-xs text-emerald-300">
                      <MapPin size={14} />
                      Standort gesetzt: {notificationLatLng.latitude.toFixed(4)},{' '}
                      {notificationLatLng.longitude.toFixed(4)}
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <p className="text-white font-medium">Benachrichtigungsradius</p>
                    <span className="text-sm text-[#3fced1] font-semibold">
                      {notificationForm.notificationDistance ?? 100} km
                    </span>
                  </div>
                  <Slider
                    aria-label="Benachrichtigungsradius"
                    size="sm"
                    minValue={10}
                    maxValue={500}
                    step={10}
                    value={notificationForm.notificationDistance ?? 100}
                    onChange={(v) =>
                      setNotificationForm((s) => ({
                        ...s,
                        notificationDistance: Array.isArray(v) ? v[0] : (v as number),
                      }))
                    }
                    classNames={{
                      track: 'bg-zinc-800',
                      filler: 'bg-[#3fced1]',
                      thumb: 'bg-[#3fced1]',
                    }}
                  />
                  <p className="text-xs text-gray-500">
                    Ab welchem Umkreis sollen wir dich über Events in der Nähe informieren?
                  </p>
                </div>

                <FeedbackBanner feedback={notificationsFeedback} />

                <div className="flex justify-end">
                  <Button
                    className="bg-[#3fced1] hover:bg-[#35b5b8] text-black font-semibold"
                    startContent={<Save size={16} />}
                    isLoading={isSavingNotifications}
                    onPress={saveNotifications}
                  >
                    Speichern
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Tab>

          {/* Öffentliches Profil */}
          <Tab
            key="public"
            title={
              <div className="flex items-center gap-2">
                <Globe size={16} />
                <span>Öffentliches Profil</span>
              </div>
            }
          >
            <div className="flex flex-col gap-6 mt-6">
              {/* Spielerprofil-Einstellungen */}
              <Card className="bg-zinc-900/80 border border-zinc-800">
                <CardHeader className="px-6 pt-6 pb-2 flex items-center justify-between gap-3 flex-wrap">
                  <h2 className="text-lg font-semibold text-white">Öffentliches Spielerprofil</h2>
                  {!!publicForm.isPublic && user?.username && (
                    <RouterLink
                      to={`/spieler/${encodeURIComponent(user.username)}`}
                      className="inline-flex items-center gap-1.5 text-sm text-[#3fced1] hover:text-[#35b5b8]"
                    >
                      <ExternalLink size={14} />
                      Mein öffentliches Profil ansehen
                    </RouterLink>
                  )}
                </CardHeader>
                <CardBody className="px-6 pb-6 pt-2 flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium">Profil öffentlich sichtbar</p>
                      <p className="text-xs text-gray-400">
                        Wenn aktiv, kann dein Profil unter <code>/spieler/{user?.username}</code>{' '}
                        gefunden werden.
                      </p>
                    </div>
                    <Switch
                      isSelected={!!publicForm.isPublic}
                      onValueChange={(v) => setPublicForm((s) => ({ ...s, isPublic: v }))}
                      color="primary"
                    />
                  </div>
                  <Textarea
                    label="Über mich"
                    placeholder="Erzähle etwas über dich und deine Dart-Karriere …"
                    value={publicForm.bio ?? ''}
                    onValueChange={(v) => setPublicForm((s) => ({ ...s, bio: v }))}
                    maxLength={500}
                    minRows={3}
                    variant="bordered"
                    classNames={inputClassNames}
                  />
                  <p className="text-xs text-gray-500 -mt-2">
                    {publicForm.bio?.length ?? 0} / 500 Zeichen
                  </p>

                  <Divider className="bg-zinc-800" />

                  <div>
                    <p className="text-white font-medium mb-1">Social Media</p>
                    <p className="text-xs text-gray-400 mb-3">
                      Benutzernamen oder Links – werden auf deinem öffentlichen Profil angezeigt.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Input
                        label="Instagram"
                        placeholder="@benutzername"
                        value={socialForm.instagram ?? ''}
                        onValueChange={(v) => setSocialForm((s) => ({ ...s, instagram: v }))}
                        startContent={<Camera size={16} className="text-gray-400" />}
                        variant="bordered"
                        maxLength={100}
                        classNames={inputClassNames}
                      />
                      <Input
                        label="Facebook"
                        placeholder="Profilname"
                        value={socialForm.facebook ?? ''}
                        onValueChange={(v) => setSocialForm((s) => ({ ...s, facebook: v }))}
                        startContent={<Hash size={16} className="text-gray-400" />}
                        variant="bordered"
                        maxLength={100}
                        classNames={inputClassNames}
                      />
                      <Input
                        label="X / Twitter"
                        placeholder="@benutzername"
                        value={socialForm.twitter ?? ''}
                        onValueChange={(v) => setSocialForm((s) => ({ ...s, twitter: v }))}
                        startContent={<AtSign size={16} className="text-gray-400" />}
                        variant="bordered"
                        maxLength={100}
                        classNames={inputClassNames}
                      />
                      <Input
                        label="TikTok"
                        placeholder="@benutzername"
                        value={socialForm.tiktok ?? ''}
                        onValueChange={(v) => setSocialForm((s) => ({ ...s, tiktok: v }))}
                        startContent={<Music size={16} className="text-gray-400" />}
                        variant="bordered"
                        maxLength={100}
                        classNames={inputClassNames}
                      />
                      <Input
                        label="YouTube"
                        placeholder="Kanalname"
                        value={socialForm.youtube ?? ''}
                        onValueChange={(v) => setSocialForm((s) => ({ ...s, youtube: v }))}
                        startContent={<PlayCircle size={16} className="text-gray-400" />}
                        variant="bordered"
                        maxLength={100}
                        classNames={inputClassNames}
                      />
                    </div>
                  </div>

                  <FeedbackBanner feedback={publicFeedback} />

                  <div className="flex justify-end">
                    <Button
                      className="bg-[#3fced1] hover:bg-[#35b5b8] text-black font-semibold"
                      startContent={<Save size={16} />}
                      isLoading={isSavingPublic}
                      onPress={savePublicProfile}
                    >
                      Speichern
                    </Button>
                  </div>
                </CardBody>
              </Card>

              {/* Header-Bild */}
              <Card className="bg-zinc-900/80 border border-zinc-800">
                <CardHeader className="px-6 pt-6 pb-2">
                  <h2 className="text-lg font-semibold text-white">Header-Bild</h2>
                </CardHeader>
                <CardBody className="px-6 pb-6 pt-2 flex flex-col gap-4">
                  <p className="text-xs text-gray-400 -mt-1">
                    Das Header-Bild wird oben auf deinem öffentlichen Profil angezeigt.
                  </p>
                  <div className="relative h-40 w-full rounded-xl overflow-hidden border border-zinc-800 bg-zinc-950 flex items-center justify-center">
                    {publicProfile?.headerImage ? (
                      <img
                        src={userService.getFileUrl(publicProfile.headerImage) ?? undefined}
                        alt="Header-Bild Vorschau"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex flex-col items-center gap-2 text-gray-600">
                        <ImageIcon size={32} />
                        <span className="text-xs">Kein Header-Bild gesetzt</span>
                      </div>
                    )}
                  </div>
                  <FeedbackBanner feedback={headerFeedback} />
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      className="bg-[#3fced1] hover:bg-[#35b5b8] text-black font-semibold"
                      startContent={<Upload size={16} />}
                      isLoading={isUploadingHeader}
                      onPress={() => headerFileInputRef.current?.click()}
                    >
                      {publicProfile?.headerImage ? 'Bild ersetzen' : 'Bild hochladen'}
                    </Button>
                    {publicProfile?.headerImage && (
                      <Button
                        color="danger"
                        variant="flat"
                        startContent={<Trash2 size={16} />}
                        isDisabled={isUploadingHeader}
                        onPress={removeHeaderImage}
                      >
                        Entfernen
                      </Button>
                    )}
                  </div>
                </CardBody>
              </Card>

              {/* Dart-Setups */}
              <Card className="bg-zinc-900/80 border border-zinc-800">
                <CardHeader className="px-6 pt-6 pb-2 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Target size={18} className="text-[#3fced1]" />
                    <h2 className="text-lg font-semibold text-white">
                      Meine Dart-Setups ({dartSetups.length}/10)
                    </h2>
                  </div>
                  <Button
                    size="sm"
                    className="bg-[#3fced1] hover:bg-[#35b5b8] text-black font-semibold"
                    startContent={<Plus size={16} />}
                    isDisabled={dartSetups.length >= 10}
                    onPress={openCreateSetup}
                  >
                    Setup hinzufügen
                  </Button>
                </CardHeader>
                <CardBody className="px-6 pb-6 pt-2 flex flex-col gap-4">
                  <FeedbackBanner feedback={setupListFeedback} />
                  {dartSetups.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-zinc-700 py-10 text-center text-gray-500">
                      <Target size={32} className="mx-auto mb-2 text-gray-600" />
                      <p className="text-sm">Du hast noch keine Dart-Setups angelegt.</p>
                      <p className="text-xs mt-1">
                        Zeige der Community, mit welchen Darts du spielst.
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {dartSetups.map((setup) => (
                        <div
                          key={setup.id}
                          className="rounded-xl border border-zinc-800 bg-zinc-950 overflow-hidden flex flex-col"
                        >
                          <RouterLink
                            to={`/setup/${encodeURIComponent(setup.id)}`}
                            className="aspect-square bg-zinc-900 flex items-center justify-center overflow-hidden"
                          >
                            {setup.image ? (
                              <img
                                src={userService.getFileUrl(setup.image) ?? undefined}
                                alt={setup.title ?? 'Dart-Setup'}
                                className="w-full h-full object-cover hover:scale-105 transition-transform"
                              />
                            ) : (
                              <ImageIcon size={32} className="text-gray-600" />
                            )}
                          </RouterLink>
                          <div className="p-3 flex flex-col gap-1 flex-1">
                            <p className="text-sm font-semibold text-white truncate">
                              {setup.title || 'Setup'}
                            </p>
                            {setup.subtitle && (
                              <p className="text-xs text-gray-400 truncate">{setup.subtitle}</p>
                            )}
                            <div className="flex gap-2 mt-auto pt-2">
                              <Button
                                size="sm"
                                variant="bordered"
                                className="border-zinc-700 text-gray-300 flex-1"
                                startContent={<Pencil size={14} />}
                                onPress={() => openEditSetup(setup)}
                              >
                                Bearbeiten
                              </Button>
                              <Button
                                size="sm"
                                color="danger"
                                variant="flat"
                                isIconOnly
                                isLoading={deletingSetupId === setup.id}
                                onPress={() => removeSetup(setup)}
                                aria-label="Setup löschen"
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardBody>
              </Card>
            </div>
          </Tab>

          {/* Sicherheit */}
          <Tab
            key="security"
            title={
              <div className="flex items-center gap-2">
                <KeyRound size={16} />
                <span>Sicherheit</span>
              </div>
            }
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              <Card className="bg-zinc-900/80 border border-zinc-800">
                <CardHeader className="px-6 pt-6 pb-2">
                  <h2 className="text-lg font-semibold text-white">Passwort ändern</h2>
                </CardHeader>
                <CardBody className="px-6 pb-6 pt-2 flex flex-col gap-4">
                  <Input
                    type={showPw ? 'text' : 'password'}
                    label="Aktuelles Passwort"
                    value={pwForm.currentPassword}
                    onValueChange={(v) => setPwForm((s) => ({ ...s, currentPassword: v }))}
                    autoComplete="current-password"
                    startContent={<KeyRound size={16} className="text-gray-400" />}
                    endContent={
                      <button
                        type="button"
                        onClick={() => setShowPw((v) => !v)}
                        className="text-gray-400 hover:text-white"
                        aria-label={showPw ? 'Passwörter verbergen' : 'Passwörter anzeigen'}
                      >
                        {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    }
                    variant="bordered"
                    classNames={inputClassNames}
                  />
                  <Input
                    type={showPw ? 'text' : 'password'}
                    label="Neues Passwort"
                    value={pwForm.newPassword}
                    onValueChange={(v) => setPwForm((s) => ({ ...s, newPassword: v }))}
                    autoComplete="new-password"
                    description="Mindestens 6 Zeichen."
                    startContent={<KeyRound size={16} className="text-gray-400" />}
                    variant="bordered"
                    classNames={inputClassNames}
                  />
                  <Input
                    type={showPw ? 'text' : 'password'}
                    label="Neues Passwort bestätigen"
                    value={pwForm.confirm}
                    onValueChange={(v) => setPwForm((s) => ({ ...s, confirm: v }))}
                    autoComplete="new-password"
                    startContent={<KeyRound size={16} className="text-gray-400" />}
                    variant="bordered"
                    classNames={inputClassNames}
                  />

                  <FeedbackBanner feedback={pwFeedback} />

                  <div className="flex justify-end">
                    <Button
                      className="bg-[#3fced1] hover:bg-[#35b5b8] text-black font-semibold"
                      isLoading={isChangingPw}
                      onPress={changePassword}
                    >
                      Passwort ändern
                    </Button>
                  </div>
                </CardBody>
              </Card>

              <Card className="bg-zinc-900/80 border border-red-900/40">
                <CardHeader className="px-6 pt-6 pb-2">
                  <div className="flex items-center gap-2">
                    <ShieldAlert size={18} className="text-red-400" />
                    <h2 className="text-lg font-semibold text-white">Gefahrenzone</h2>
                  </div>
                </CardHeader>
                <CardBody className="px-6 pb-6 pt-2 flex flex-col gap-3">
                  <p className="text-sm text-gray-400">
                    Wenn du deinen Account löschst, werden deine persönlichen Daten entfernt.
                    Diese Aktion kann nicht rückgängig gemacht werden.
                  </p>
                  <FeedbackBanner feedback={deleteFeedback} />
                  <Button
                    color="danger"
                    variant="flat"
                    startContent={<Trash2 size={16} />}
                    onPress={deleteModal.onOpen}
                    className="w-fit"
                  >
                    Account löschen
                  </Button>
                </CardBody>
              </Card>
            </div>
          </Tab>
        </Tabs>
      </div>

      <Modal
        isOpen={deleteModal.isOpen}
        onOpenChange={deleteModal.onOpenChange}
        backdrop="blur"
        classNames={{
          base: 'bg-zinc-900 border border-zinc-800',
          header: 'text-white',
          body: 'text-gray-300',
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex items-center gap-2">
                <ShieldAlert size={18} className="text-red-400" />
                Account wirklich löschen?
              </ModalHeader>
              <ModalBody>
                <p>
                  Diese Aktion ist endgültig. Dein Profil, deine Einstellungen und deine
                  persönlichen Daten werden gelöscht.
                </p>
                <FeedbackBanner feedback={deleteFeedback} />
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={onClose} className="text-gray-300">
                  Abbrechen
                </Button>
                <Button
                  color="danger"
                  onPress={confirmDelete}
                  isLoading={isDeleting}
                  startContent={<Trash2 size={16} />}
                >
                  Endgültig löschen
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Dart-Setup Modal (Erstellen / Bearbeiten) */}
      <Modal
        isOpen={setupModal.isOpen}
        onOpenChange={setupModal.onOpenChange}
        backdrop="blur"
        size="lg"
        classNames={{
          base: 'bg-zinc-900 border border-zinc-800',
          header: 'text-white',
          body: 'text-gray-300',
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex items-center gap-2">
                <Target size={18} className="text-[#3fced1]" />
                {editingSetup ? 'Setup bearbeiten' : 'Neues Dart-Setup'}
              </ModalHeader>
              <ModalBody className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <p className="text-sm text-gray-300">Bild {editingSetup ? '' : '(Pflicht)'}</p>
                  <button
                    type="button"
                    onClick={() => setupFileInputRef.current?.click()}
                    className="relative h-44 w-full rounded-xl overflow-hidden border border-dashed border-zinc-700 hover:border-[#3fced1] bg-zinc-950 flex items-center justify-center transition-colors"
                  >
                    {setupImagePreview ? (
                      <img
                        src={setupImagePreview}
                        alt="Setup-Vorschau"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex flex-col items-center gap-2 text-gray-500">
                        <Upload size={28} />
                        <span className="text-xs">Bild auswählen (max. 5 MB)</span>
                      </div>
                    )}
                  </button>
                  {setupImagePreview && (
                    <p className="text-xs text-gray-500">
                      Zum Ändern auf das Bild klicken.
                    </p>
                  )}
                </div>
                <Input
                  label="Titel"
                  placeholder="z. B. Target Swiss Point 24g"
                  value={setupForm.title}
                  onValueChange={(v) => setSetupForm((s) => ({ ...s, title: v }))}
                  maxLength={100}
                  isRequired
                  variant="bordered"
                  classNames={inputClassNames}
                />
                <Input
                  label="Untertitel"
                  placeholder="z. B. Marke · Gewicht · Barrel-Typ"
                  value={setupForm.subtitle}
                  onValueChange={(v) => setSetupForm((s) => ({ ...s, subtitle: v }))}
                  maxLength={100}
                  variant="bordered"
                  classNames={inputClassNames}
                />
                <Textarea
                  label="Beschreibung"
                  placeholder="Erzähle etwas über dein Setup (Flights, Shafts, Spitzen …)"
                  value={setupForm.description}
                  onValueChange={(v) => setSetupForm((s) => ({ ...s, description: v }))}
                  maxLength={500}
                  minRows={3}
                  variant="bordered"
                  classNames={inputClassNames}
                />
                <p className="text-xs text-gray-500 -mt-2">
                  {setupForm.description.length} / 500 Zeichen
                </p>
                <FeedbackBanner feedback={setupModalFeedback} />
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={onClose} className="text-gray-300">
                  Abbrechen
                </Button>
                <Button
                  className="bg-[#3fced1] hover:bg-[#35b5b8] text-black font-semibold"
                  startContent={<Save size={16} />}
                  isLoading={isSavingSetup}
                  onPress={saveSetup}
                >
                  {editingSetup ? 'Speichern' : 'Erstellen'}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default ProfilePage;
